import { NavLink, Outlet, useParams } from 'react-router-dom'
import { useRoom, useSetRoomStatus } from '../api/rooms'
import { ApiError } from '../api/client'
import { errorMessage } from '../lib/format'
import type { RoomResponse, RoomStatus } from '../api/types'
import { NdaGate } from './NdaGate'
import { useAuth } from '../auth/AuthContext'
import { EmailPreferences } from './EmailPreferences'
import { CloseoutPanel } from './CloseoutPanel'

/**
 * Everything under /rooms/:roomId shares this header: the room, its status, the
 * lifecycle controls (owners), and the tabs. The room comes from the server with the
 * caller's own bits, so the buttons on it are the server's answer, not the client's guess.
 */
export function RoomLayout() {
  const { roomId = '' } = useParams()
  const room = useRoom(roomId)
  // The reports sit behind the CanAudit realm-role gate as well as the room bit, so the
  // tab is offered only when both agree; otherwise a view_audit grant on a reviewer would
  // show a tab that answers 403 (found in the Phase 4 review, 2026-09-10).
  const { me } = useAuth()
  const canAuditRole = ['auditor', 'room-owner', 'platform-admin'].some((r) =>
    me?.roles.includes(r),
  )

  if (room.isPending) return <p className="muted">Loading room…</p>
  if (room.isError) {
    const status = room.error instanceof ApiError ? room.error.status : null
    return (
      <div className="alert alert-error" role="alert">
        <p className="alert-title">
          {status === 404 ? 'This room is not available to you.' : 'Could not load the room.'}
        </p>
        <p>{errorMessage(room.error)}</p>
      </div>
    )
  }

  // The server's answer: a participant who has not accepted the current NDA has no bits
  // at all, and the NDA text is the whole room until they do.
  if (room.data.nda.required && !room.data.nda.accepted && room.data.memberRole !== 'Owner') {
    return (
      <div className="stack">
        <RoomHeader room={room.data} />
        <NdaGate room={room.data} />
      </div>
    )
  }

  return (
    <div className="stack">
      <RoomHeader room={room.data} />
      <CloseoutPanel room={room.data} />
      <nav className="tabs" aria-label="Room sections">
        <NavLink to="" end className={({ isActive }) => (isActive ? 'tab tab-active' : 'tab')}>
          Documents
        </NavLink>
        <NavLink to="questions" className={({ isActive }) => (isActive ? 'tab tab-active' : 'tab')}>
          Q&amp;A
        </NavLink>
        <NavLink to="checklist" className={({ isActive }) => (isActive ? 'tab tab-active' : 'tab')}>
          Checklist
        </NavLink>
        {(room.data.memberRole === 'Owner' || room.data.permissions.managePermissions) && (
          <>
            <NavLink
              to="people"
              className={({ isActive }) => (isActive ? 'tab tab-active' : 'tab')}
            >
              People
            </NavLink>
            <NavLink
              to="permissions"
              className={({ isActive }) => (isActive ? 'tab tab-active' : 'tab')}
            >
              Permissions
            </NavLink>
          </>
        )}
        {canAuditRole && (room.data.memberRole === 'Owner' || room.data.permissions.viewAudit) && (
          <NavLink
            to="activity"
            className={({ isActive }) => (isActive ? 'tab tab-active' : 'tab')}
          >
            Activity
          </NavLink>
        )}
        {room.data.memberRole === 'Owner' && (
          <NavLink
            to="settings"
            className={({ isActive }) => (isActive ? 'tab tab-active' : 'tab')}
          >
            Settings
          </NavLink>
        )}
      </nav>
      <Outlet context={room.data} />
    </div>
  )
}

const TRANSITIONS: Record<RoomStatus, { to: RoomStatus; label: string; confirm: string }[]> = {
  Draft: [{ to: 'Open', label: 'Open the room', confirm: 'Open this room to its participants?' }],
  Open: [
    {
      to: 'Frozen',
      label: 'Freeze',
      confirm:
        'Freeze this room? Nobody, owners included, can change anything until it is reopened.',
    },
  ],
  Frozen: [
    { to: 'Open', label: 'Reopen', confirm: 'Reopen this room for changes?' },
    {
      to: 'Archived',
      label: 'Archive',
      confirm:
        'Archive this room? This is final: participants lose access and owners keep read-only access.',
    },
  ],
  Archived: [],
}

function RoomHeader({ room }: { room: RoomResponse }) {
  const setStatus = useSetRoomStatus(room.id)

  return (
    <header className="room-header">
      <div>
        <p className="small muted room-org">{room.organizationName}</p>
        <h1 className="h1">{room.name}</h1>
        {room.description && <p className="muted room-desc">{room.description}</p>}
      </div>
      <div className="room-header-side">
        <span className={`status status-${room.status.toLowerCase()}`}>{room.status}</span>
        <EmailPreferences roomId={room.id} />
        {room.memberRole === 'Owner' &&
          TRANSITIONS[room.status].map((t) => (
            <button
              key={t.to}
              type="button"
              className="btn"
              disabled={setStatus.isPending}
              onClick={() => {
                if (window.confirm(t.confirm)) setStatus.mutate(t.to)
              }}
            >
              {t.label}
            </button>
          ))}
        {setStatus.isError && (
          <span className="small error-text">{errorMessage(setStatus.error)}</span>
        )}
      </div>
    </header>
  )
}
